import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PaginatedResponse } from '../_DTOs/IPaginaterResponse';
import { NestedQueryDataRequest, StructuredDataRequest } from '../_DTOs/IStructuredData';
import { IEntityMetaData } from '../_models/IEntityMetaData';
import { IEntityRepository } from './IRepository';
import { EntityBase } from '../_models/EntityBase';
import { MatwinsVariablesService } from '../services/matwinsVariables.service';
import { ResponseHandlerService } from '../services/ResponseHandlerService';
import { LookupContext } from '../services/LookupService';
import { BrowserIdService } from '../services/browser-id.service';

export class ApiRepository<T extends EntityBase> implements IEntityRepository<T> {
  private metaData!: IEntityMetaData;
  private baseUrl!: string;


  constructor(
    protected http: HttpClient,
    protected variables: MatwinsVariablesService,
    protected responseHandler: ResponseHandlerService,
    protected browserIdService: BrowserIdService
  ) { }


  private get defaultHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
     // 'Content-Type': 'application/json',
      'X-Custom-Matwins-BrowserId': this.variables.browserId || '',
      'X-Custom-Matwins-ClientId': this.variables.clientId || '',
      'X-Custom-Matwins-PosId': this.variables.posId?.toString() || ''
    });

    if (this.metaData.UseTokenCheck) {
      headers = headers.set('X-Custom-Matwins-UseAuthentication', this.variables.useAuthentication.toString());
      headers = headers.set('X-Custom-Matwins-TenantPrefix', this.variables.tenantPrefix || '');
      const accessToken = this.variables.getAccessToken();
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`);
      }
    }
    return headers;
  }

  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  setMetaData(metaData: IEntityMetaData) {
    this.metaData = metaData;
  }

  getMetaData(): IEntityMetaData {
    return this.metaData;
  }

  protected getEndpoint(): string {
    return `${this.baseUrl}${this.metaData.EndPoint}`;
  }

  private toDataRequest(params: StructuredDataRequest, requiredLookups: LookupContext[] = []): StructuredDataRequest {
    return {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      search: params.search || null,
      sortBy: params.sortBy || null,
      sortOrder: params.sortOrder || 'asc',
      searchColumns: params.searchColumns || null,
      requiredLookups: requiredLookups,
      dateRange: params.dateRange || undefined,
      filters: params.filters || undefined,
      localMatchedIds: params.localMatchedIds || undefined,
      featureContext: params.featureContext,
      includedEntities: params.includedEntities
    };
  }

  async getAll(params: StructuredDataRequest, requiredLookups: LookupContext[] = []): Promise<PaginatedResponse<T>> {
    const dataRequest = this.toDataRequest(params, requiredLookups);
    const url = `${this.getEndpoint()}`;

    try {
      const response = await firstValueFrom(
        this.http.post<PaginatedResponse<T>>(url, dataRequest, { headers: this.defaultHeaders, observe: 'response' })
      );

      this.responseHandler.handleSuccess();

      if (response.body === null || response.body === undefined) {
        throw new Error('Empty response body');
      }

      return {
        items: response.body.items || [],
        total: response.body.total || 0
      };

    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  // Interroge la base du client : les listes imbriquees n'ont pas de contexte locataire,
  // ce point d'entree le leur fournit avec le client et le point de service.
  async queryNested(request: NestedQueryDataRequest): Promise<PaginatedResponse<T>> {
    const url = `${this.baseUrl}/nestedquery/default`;

    try {
      const response = await firstValueFrom(
        this.http.post<PaginatedResponse<T>>(url, request, { headers: this.defaultHeaders, observe: 'response' })
      );

      this.responseHandler.handleSuccess();

      if (response.body === null || response.body === undefined) {
        throw new Error('Empty response body');
      }

      return {
        items: response.body.items || [],
        total: response.body.total || 0,
        truncated: response.body.truncated,
        matchedIds: response.body.matchedIds
      };
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async get(id: number): Promise<T> {
    try {
      const response = await firstValueFrom(
        this.http.get<T>(`${this.getEndpoint()}/${id}`, {
          headers: this.defaultHeaders,
          observe: 'response'
        })
      );

      this.responseHandler.handleSuccess();

      if (response.body === null || response.body === undefined) {
        throw new Error('Empty response body');
      }

      return response.body;
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async getBySearchArgs(params: StructuredDataRequest, search: any): Promise<PaginatedResponse<T>> {
    const dataRequest = this.toDataRequest(params, []);
    const url = `${this.getEndpoint()}/search`;

    try {
      const response = await firstValueFrom(
        this.http.post<PaginatedResponse<T>>(url, { ...dataRequest, searchCriteria: search }, {
          headers: this.defaultHeaders,
          observe: 'response'
        })
      );

      this.responseHandler.handleSuccess();

      if (response.body === null || response.body === undefined) {
        throw new Error('Empty response body');
      }

      return {
        items: response.body.items || [],
        total: response.body.total || 0
      };
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async execute<U>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', urlExecuteMethod: string, data?: any): Promise<U | null> {
    const url = `${this.getEndpoint()}/${urlExecuteMethod}`;

    try {
      let response: HttpResponse<U>;

      switch (method.toUpperCase()) {
        case 'GET':
          const queryParams = data ? this.buildQueryParams(data) : '';
          const getUrl = queryParams ? `${url}?${queryParams}` : url;
          response = await firstValueFrom(
            this.http.get<U>(getUrl, {
              headers: this.defaultHeaders,
              observe: 'response'
            })
          );
          break;

        case 'POST':
          response = await firstValueFrom(
            this.http.post<U>(url, data, {
              headers: this.defaultHeaders,
              observe: 'response'
            })
          );
          break;

        case 'PUT':
          response = await firstValueFrom(
            this.http.put<U>(url, data, {
              headers: this.defaultHeaders,
              observe: 'response'
            })
          );
          break;

        case 'DELETE':
          response = await firstValueFrom(
            this.http.delete<U>(url, {
              headers: this.defaultHeaders,
              observe: 'response'
            })
          );
          break;

        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      this.responseHandler.handleSuccess();

      // if (response.body === null || response.body === undefined) {
      //   throw new Error('Empty response body');
      // }

      return response.body;
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async download<U>(urlExecuteMethod: string, data?: any): Promise<U | null> {
    const url = `${this.getEndpoint()}/${urlExecuteMethod}`;

    try {
      let response: HttpResponse<U>;

      
          response = await firstValueFrom(
            this.http.post<U>(url, data, {
              headers: this.defaultHeaders,
              responseType: 'blob' as 'json', // Cast to 'json' pour éviter les erreurs de type, mais le type réel est 'blob'
              observe: 'response'
            })
          );
         

      this.responseHandler.handleSuccess();

      // if (response.body === null || response.body === undefined) {
      //   throw new Error('Empty response body');
      // }

      return response.body;
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  private buildQueryParams(params: any): string {
    return Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}=${value.map(v => encodeURIComponent(String(v))).join(',')}`;
        }
        return `${key}=${encodeURIComponent(String(value))}`;
      })
      .join('&');
  }

  async create(entity: T, componentId?: string): Promise<T> {
    let headers = this.defaultHeaders;
    if (componentId) {
      headers = headers.set('X-Custom-Matwins-ComponentId', componentId);
    }

    try {
      const response = await firstValueFrom(
        this.http.post<T>(this.getEndpoint(), entity, {
          headers: headers,
          observe: 'response'
        })
      );

      this.responseHandler.handleSuccess('Created successfully');

      if (response.body === null || response.body === undefined) {
        throw new Error('Empty response body');
      }

      return response.body;
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async update(id: number, entity: T, componentId?: string): Promise<void> {
    let headers = this.defaultHeaders;
    if (componentId) {
      headers = headers.set('X-Custom-Matwins-ComponentId', componentId);
    }

    try {
      await firstValueFrom(
        this.http.put<void>(`${this.getEndpoint()}/${id}`, entity, {
          headers: headers,
          observe: 'response'
        })
      );
      this.responseHandler.handleSuccess('Updated successfully');
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async delete(id: number, componentId?: string): Promise<void> {
    let headers = this.defaultHeaders;
    if (componentId) {
      headers = headers.set('X-Custom-Matwins-ComponentId', componentId);
    }

    try {
      await firstValueFrom(
        this.http.delete<void>(`${this.getEndpoint()}/${id}`, {
          headers: headers,
          observe: 'response'
        })
      );
      this.responseHandler.handleSuccess('Deleted successfully');
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }


  async getAllWithLookups(params: StructuredDataRequest, requiredLookups: LookupContext[] = []): Promise<PaginatedResponse<T>> {
    const dataRequest = this.toDataRequest(params, requiredLookups);
    const url = `${this.getEndpoint()}/default`;

    try {
      const response = await firstValueFrom(
        this.http.post<PaginatedResponse<T>>(url, dataRequest, {
          headers: this.defaultHeaders,
          observe: 'response'
        })
      );

      this.responseHandler.handleSuccess();

      if (response.body === null || response.body === undefined) {
        throw new Error('Empty response body');
      }

      return response.body;
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }

  async fetchLookups(lookupContexts: LookupContext[]): Promise<any> {
    const url = `${this.getEndpoint()}/lookups`;

    try {
      const response = await firstValueFrom(
        this.http.post<any>(url, lookupContexts, { headers: this.defaultHeaders, observe: 'response' })
      );

      this.responseHandler.handleSuccess();

      if (!response.body) {
        throw new Error('Empty response body');
      }

      return response.body;
    } catch (error) {
      this.responseHandler.handleError(error);
      throw error;
    }
  }
}
